<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useCartStore } from "../stores/cart";
import VdIcon from "./VdIcon.vue";
import { useAuthStore } from "../stores/auth";
import AssistantLaunch from "./AssistantLaunch.vue";

const route = useRoute();
const cart = useCartStore();
const auth = useAuthStore();
const cartCount = computed(() => cart.itemsCount);

interface Item {
  to: string;
  label: string;
  icon: string;
  match: (path: string) => boolean;
}

// 5 реальных разделов приложения, переверстаны в визуальный язык спеки §2.6
// (Tabler outline). «Избранного» в приложении нет — фантомный пункт не добавляем.
// Гость → единый экран входа (=вступление в лояльность), а не отдельный лендинг-дубль. Авторизованный → профиль.
const profileTarget = computed(() => (auth.isAuthed ? "/profile" : "/auth"));

const items = computed<Item[]>(() => [
  { to: "/", label: "Каталог", icon: "building-store", match: (p) => p === "/" || p.startsWith("/c/") || p.startsWith("/p/") },
  { to: "/recipes", label: "Рецепты", icon: "book", match: (p) => p.startsWith("/recipes") },
  { to: "/cart", label: "Корзина", icon: "shopping-cart", match: (p) => p.startsWith("/cart") || p.startsWith("/checkout") },
  { to: profileTarget.value, label: "Профиль", icon: "user", match: (p) => p.startsWith("/profile") || p.startsWith("/loyalty") },
]);

// Высота меню → --vd-nav-h: нижние строки действий (.vd-dock) садятся ровно на его верх при любом масштабе
// (Ctrl±), safe-area и переходе через порог md (меню скрыто → 0). Игорь 01.10: строка «висела в воздухе».
const navEl = ref<HTMLElement | null>(null);
let ro: ResizeObserver | null = null;
function publishNavHeight(): void {
  const h = navEl.value ? navEl.value.getBoundingClientRect().height : 0;
  document.documentElement.style.setProperty("--vd-nav-h", `${Math.round(h)}px`);
}
onMounted(() => {
  publishNavHeight();
  if (typeof ResizeObserver !== "undefined" && navEl.value) {
    ro = new ResizeObserver(publishNavHeight);
    ro.observe(navEl.value);
  }
  window.addEventListener("resize", publishNavHeight);
});
onBeforeUnmount(() => {
  ro?.disconnect();
  window.removeEventListener("resize", publishNavHeight);
  document.documentElement.style.setProperty("--vd-nav-h", "0px");
});

function isActive(item: Item): boolean {
  return item.match(route.path);
}
</script>

<template>
  <nav
    ref="navEl"
    data-bottom-nav
    class="fixed inset-x-0 bottom-0 z-30 border-t border-vd-line bg-vd-card pb-[env(safe-area-inset-bottom)] md:hidden"
  >
    <ul class="grid grid-cols-5 pb-2 pt-2.5">
      <li v-for="item in items" :key="item.to" class="contents">
        <RouterLink
          :to="item.to"
          class="relative flex flex-col items-center justify-center gap-1 py-0.5"
          :class="isActive(item) ? 'text-vd-brand' : 'text-vd-muted'"
        >
          <VdIcon :name="item.icon" :size="22" :stroke="isActive(item) ? 2.1 : 1.9" />
          <span class="text-[13px]" :class="isActive(item) ? 'font-medium' : ''">{{ item.label }}</span>
          <span
            v-if="item.to === '/cart' && cartCount > 0"
            class="absolute right-[22%] top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[12px] font-medium text-white"
          >{{ cartCount }}</span>
        </RouterLink>
      </li>
      <li><AssistantLaunch compact /></li>
    </ul>
  </nav>
</template>
